/*
 * The CRM's calendar arithmetic, on plain "YYYY-MM-DD" day strings: the same
 * shape <input type="date"> spoke, so every caller that stored one keeps it.
 *
 * Days are calendar days, never instants. Every Date made here sits at local
 * noon so a DST change can never push a day across midnight.
 *
 * Pure, so the rules are tested without a browser (test/datepicker.test.js).
 */

// His week starts on Sunday (Israel), as the browser's own picker showed it.
export const WEEK_START = 0;
export const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const pad = (n) => String(n).padStart(2, "0");

export function ymd(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/* "2026-10-21" → a Date at local noon; anything else → null. */
export function parse(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3], 12);
  return d.getMonth() === +m[2] - 1 ? d : null;
}

export const isDay = (s) => !!parse(s);
export const todayYmd = (now = new Date()) => ymd(now);

export function addDays(s, n) {
  const d = parse(s);
  d.setDate(d.getDate() + n);
  return ymd(d);
}

/* 31 Jan + 1 month is 28/29 Feb, not 3 Mar. */
export function addMonths(s, n) {
  const d = parse(s);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0, 12).getDate();
  d.setDate(Math.min(day, last));
  return ymd(d);
}

/* The first of the month a day is in, and of the months around it. */
export const monthStart = (s) => `${s.slice(0, 7)}-01`;
export const monthEnd = (s) => addDays(addMonths(monthStart(s), 1), -1);

export function monthTitle(s) {
  const d = parse(s);
  return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
}

/*
 * The days a month's grid shows: whole weeks from the week the 1st falls in,
 * always six rows so the popover never changes height as you page through.
 */
export function monthGrid(s) {
  const first = monthStart(s);
  const lead = (parse(first).getDay() - WEEK_START + 7) % 7;
  const start = addDays(first, -lead);
  const month = first.slice(0, 7);
  const weeks = [];
  for (let w = 0; w < 6; w += 1) {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      const day = addDays(start, w * 7 + i);
      week.push({ day, inMonth: day.slice(0, 7) === month });
    }
    weeks.push(week);
  }
  return weeks;
}

export function allowed(s, min, max) {
  if (min && s < min) return false;
  if (max && s > max) return false;
  return true;
}

export function clamp(s, min, max) {
  if (min && s < min) return min;
  if (max && s > max) return max;
  return s;
}

export function daysBetween(a, b) {
  return Math.round((parse(b) - parse(a)) / 86400000);
}

/* "Wed 21 Oct", with the year only when it is not this year's. */
export function dayLabel(s, { weekday = true, now = new Date() } = {}) {
  const d = parse(s);
  if (!d) return "";
  const year = d.getFullYear() === now.getFullYear() ? "" : ` ${d.getFullYear()}`;
  return `${weekday ? `${DAYS[d.getDay()]} ` : ""}${d.getDate()} ${MONTHS[d.getMonth()]}${year}`;
}

/* "21 Oct – 4 Nov", "28 Dec 2025 – 3 Jan 2026", or one day alone. */
export function rangeText(from, to, now = new Date()) {
  if (!from) return "";
  if (!to || to === from) return dayLabel(from, { weekday: false, now });
  const a = parse(from);
  const b = parse(to);
  const sameYear = a.getFullYear() === b.getFullYear();
  const yearA = sameYear ? "" : ` ${a.getFullYear()}`;
  const yearB = b.getFullYear() === now.getFullYear() && sameYear ? "" : ` ${b.getFullYear()}`;
  return `${a.getDate()} ${MONTHS[a.getMonth()]}${yearA} – ${b.getDate()} ${MONTHS[b.getMonth()]}${yearB}`;
}

/* A click on a day, given the pick so far: the first click starts, the second ends. */
export function pickRange(sel, day) {
  if (!sel.from || sel.to) return { from: day, to: null };
  if (day < sel.from) return { from: day, to: sel.from };
  return { from: sel.from, to: day };
}

/* Where an arrow key moves the focused day. */
export function stepDay(s, key) {
  const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[key];
  if (step) return addDays(s, step);
  if (key === "PageUp") return addMonths(s, -1);
  if (key === "PageDown") return addMonths(s, 1);
  const d = parse(s);
  const dow = (d.getDay() - WEEK_START + 7) % 7;
  if (key === "Home") return addDays(s, -dow);
  if (key === "End") return addDays(s, 6 - dow);
  return null;
}

/* ------------------------------------------------------------ presets -- */

const quarterStart = (s) => {
  const d = parse(s);
  return `${d.getFullYear()}-${pad(Math.floor(d.getMonth() / 3) * 3 + 1)}-01`;
};

/*
 * Whole-day ranges for the range panel's left column, both ends included.
 * "This …" runs to today; "Last …" is the whole calendar unit before it.
 */
const RANGE_PRESETS = {
  today: ["Today", (t) => [t, t]],
  yesterday: ["Yesterday", (t) => [addDays(t, -1), addDays(t, -1)]],
  last7: ["Last 7 days", (t) => [addDays(t, -6), t]],
  last30: ["Last 30 days", (t) => [addDays(t, -29), t]],
  last90: ["Last 90 days", (t) => [addDays(t, -89), t]],
  thisWeek: ["This week", (t) => [stepDay(t, "Home"), t]],
  lastWeek: ["Last week", (t) => [addDays(stepDay(t, "Home"), -7), addDays(stepDay(t, "Home"), -1)]],
  thisMonth: ["This month", (t) => [monthStart(t), t]],
  lastMonth: ["Last month", (t) => [monthStart(addMonths(monthStart(t), -1)), addDays(monthStart(t), -1)]],
  thisQuarter: ["This quarter", (t) => [quarterStart(t), t]],
  lastQuarter: ["Last quarter", (t) => [addMonths(quarterStart(t), -3), addDays(quarterStart(t), -1)]],
  thisYear: ["This year", (t) => [`${t.slice(0, 4)}-01-01`, t]],
  lastYear: ["Last year", (t) => [`${+t.slice(0, 4) - 1}-01-01`, `${+t.slice(0, 4) - 1}-12-31`]],
  last12m: ["Last 12 months", (t) => [addDays(addMonths(t, -12), 1), t]],
};

export function rangePresets(keys, today = todayYmd()) {
  return keys
    .filter((k) => RANGE_PRESETS[k])
    .map((key) => {
      const [label, make] = RANGE_PRESETS[key];
      const [from, to] = make(today);
      return { key, label, from, to };
    });
}

/*
 * One-click days for the single picker: { label, days } or { label, months }
 * or { label, weekday } (the next one, never today).
 */
export function quickDay(q, today = todayYmd()) {
  if (q.months) return addMonths(today, q.months);
  if (q.weekday != null) {
    const ahead = ((q.weekday - parse(today).getDay() + 7) % 7) || 7;
    return addDays(today, ahead);
  }
  return addDays(today, q.days || 0);
}
